import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import ImageIcon from './image-icon'; // Icon for default state
import { CheckCircleIcon, XCircleIcon } from 'lucide-react'; // Added XCircleIcon for remove icon
import Image from '@eliadmin/core/src/components/image/image-wrapped';

interface FileUploadBoxProps {
  name: string; // Name for react-hook-form field
  texts?: {
    placeholder?: string;
    chooseFile?: string;
    fileSelected?: string;
    successMessage?: string;
    removeFile?: string; // Text for remove file button
  };
}

const FileUploadBox: React.FC<FileUploadBoxProps> = ({ name, texts }) => {
  const form = useFormContext();
  const value = form.getValues(name);

  // Determine initial selected file
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (value?.file_url || value?.url) {
      // If value exists, initialize the preview URL and file details
      setSelectedFile({
        name: value.description || 'file',
        type: value.type || 'unknown',
        size: 0, // Size cannot be determined without downloading the file
        lastModified: 0, // Placeholder for last modified
        webkitRelativePath: '', // Optional, usually empty
      } as File);
      setPreviewUrl(value.file_url ?? value?.url);
    } else {
      setSelectedFile(value as File);
      setPreviewUrl(
        value?.type?.startsWith?.('image/')
          ? URL.createObjectURL(value)
          : value?.url
            ? value.url
            : null,
      );
    }
  }, [value]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];

      if (!isValidFileType(file)) {
        alert(
          'Neplatný typ súboru. Povolené sú len: JPG, JPEG, PNG, BMP, GIF, SVG, WEBP, PDF, DOCX, DOC, MP4',
        );
        return;
      }

      setSelectedFile(file);
      setPreviewUrl(file.type?.startsWith?.('image/') ? URL.createObjectURL(file) : null);
      form.setValue(name, file, { shouldDirty: true }); // Set file in react-hook-form context and mark dirty
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];

      if (!isValidFileType(file)) {
        alert(
          'Neplatný typ súboru. Povolené sú len: JPG, JPEG, PNG, BMP, GIF, SVG, WEBP, PDF, DOCX, DOC, MP4',
        );
        return;
      }

      setSelectedFile(file);
      setPreviewUrl(file.type?.startsWith?.('image/') ? URL.createObjectURL(file) : null);
      form.setValue(name, file, { shouldDirty: true }); // Set file in react-hook-form context and mark dirty
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null); // Clear the selected file
    setPreviewUrl(null); // Clear the preview
    form.setValue(name, null, { shouldDirty: true }); // Remove file from react-hook-form context and mark dirty
  };

  const showFileSelected = () => {
    return selectedFile?.name != 'file' && !selectedFile?.name.includes('_file');
  };

  const isImage = () => {
    if (!previewUrl) return false;

    // Check if it's a blob URL (newly uploaded file)
    if (previewUrl?.startsWith('blob:')) {
      return selectedFile?.type?.startsWith?.('image/');
    }

    // Check URL for existing images
    return (
      previewUrl.includes('jpg') ||
      previewUrl.includes('jpeg') ||
      previewUrl.includes('png') ||
      previewUrl.includes('gif') ||
      previewUrl.includes('webp') ||
      previewUrl.includes('svg') ||
      previewUrl.includes('bmp')
    );
  };

  const isValidFileType = (file: File) => {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/bmp',
      'image/gif',
      'image/svg+xml',
      'image/webp',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword', // .doc
      'video/mp4',
    ];

    const allowedExtensions = [
      '.jpg',
      '.jpeg',
      '.png',
      '.bmp',
      '.gif',
      '.svg',
      '.webp',
      '.pdf',
      '.docx',
      '.doc',
      '.mp4',
    ];

    return (
      allowedTypes.includes(file.type) ||
      allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
    );
  };

  return (
    <div
      className={`rounded-lg border-2 p-6 text-center transition-all duration-300 ${
        dragActive
          ? 'border-blue-500 bg-blue-50'
          : selectedFile
            ? 'border-green-500 bg-green-50'
            : 'border-gray-300'
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex h-full flex-col items-center justify-center">
        <div className={selectedFile ? 'text-green-500' : 'text-blue-500'}>
          {selectedFile ? <CheckCircleIcon /> : <ImageIcon />}
        </div>
        {selectedFile ? (
          <>
            <p className="mt-2 max-w-[300px] text-sm font-bold text-green-600">
              {texts?.successMessage || 'Súbor úspešne nahraný!'}
            </p>
            {previewUrl && isImage() && (
              <div className="mt-4 flex justify-center">
                <div className="relative overflow-hidden rounded-lg border border-gray-200 shadow-sm">
                  <Image
                    src={previewUrl}
                    alt="Preview"
                    className="max-h-48 max-w-48 object-contain"
                    width={192}
                    height={192}
                    sizes="192px"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 transition-opacity hover:bg-opacity-10" />
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="mt-2 max-w-[300px] text-sm text-gray-500">
            {texts?.placeholder ||
              'Vložte súbor alebo premiestnite sem JPG, PNG, PDF, DOCX, MP4 alebo iné povolené formáty (max. 60MB)'}
          </p>
        )}
        <label
          htmlFor={`file-upload-${name}`}
          className="mt-4 cursor-pointer text-blue-500 underline"
        >
          {texts?.chooseFile || (selectedFile ? 'Zmeniť súbor' : 'Vybrať súbor')}
        </label>
        <input
          id={`file-upload-${name}`}
          type="file"
          className="hidden"
          accept=".jpg,.jpeg,.png,.bmp,.gif,.svg,.webp,.pdf,.docx,.doc,.mp4"
          onChange={handleFileChange}
        />
        {selectedFile && (
          <>
            {showFileSelected() && (
              <p className="mt-2 text-sm text-gray-600">
                {texts?.fileSelected || 'Vybraný súbor'}: {selectedFile.name}
              </p>
            )}
            <button
              type="button"
              onClick={handleRemoveFile}
              className="mt-2 flex items-center gap-1 text-sm text-red-500"
            >
              <XCircleIcon className="h-4 w-4" />
              {texts?.removeFile || 'Odstrániť súbor'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default FileUploadBox;
